import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { dataService } from '../../services/DataService'

// Cache prospects globally (per company) - loaded once when dropdown opens
let prospectsCache = null
let cacheTimestamp = null
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

export const ProspectLookup = ({
  label,
  value,
  onChange,
  error,
  placeholder,
  className,
  ...props
}) => {
  const { t } = useTranslation('lookup')
  const [prospects, setProspects] = useState([])
  const [filteredProspects, setFilteredProspects] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProspect, setSelectedProspect] = useState(null)
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)

  const resolvedLabel = label || t('prospect.label')
  const resolvedPlaceholder = placeholder || t('prospect.placeholder')

  // Initialize selected prospect based on value
  useEffect(() => {
    if (value && prospects.length > 0) {
      const prospect = prospects.find(p => p.mserp_prospectid === value)
      setSelectedProspect(prospect)
    } else if (value) {
      // If we have a value but no matching prospect, create a placeholder
      setSelectedProspect({ mserp_prospectid: value, mserp_prospectname: value })
    } else {
      setSelectedProspect(null)
    }
  }, [value, prospects])

  // Filter prospects based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredProspects(prospects.slice(0, 50)) // Show first 50 prospects
    } else {
      const searchLower = searchTerm.toLowerCase()
      const filtered = prospects.filter(prospect => {
        const name = (prospect.mserp_prospectname || '').toLowerCase()
        const id = (prospect.mserp_prospectid || '').toLowerCase()
        return name.includes(searchLower) || id.includes(searchLower)
      }).slice(0, 50)
      setFilteredProspects(filtered)
    }
  }, [searchTerm, prospects])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadProspects = async () => {
    // Check cache first
    const now = Date.now()
    if (prospectsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
      console.log('Using cached prospects')
      setProspects(prospectsCache)
      setFilteredProspects(prospectsCache.slice(0, 50))
      return
    }

    setLoading(true)
    setLoadError(null)

    try {
      console.log('Loading prospects from backend...')
      const result = await dataService.getProspects()

      if (result.success && result.data) {
        console.log(`Got ${result.data.length} prospects from API`)

        // Update cache
        prospectsCache = result.data
        cacheTimestamp = now

        setProspects(result.data)
        setFilteredProspects(result.data.slice(0, 50))
      } else {
        console.error('Failed to load prospects:', result.error || 'No data')
        setLoadError(result.error || t('prospect.errorLoading', { error: '' }))
      }
    } catch (error) {
      console.error('Error loading prospects:', error)
      setLoadError(t('prospect.networkError'))
    } finally {
      setLoading(false)
    }
  }

  const handleInputClick = async () => {
    setIsOpen(true)

    // Load prospects on first click if we don't have any
    if (prospects.length === 0 && !loading) {
      await loadProspects()
    }
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setSearchTerm(newValue)
    setIsOpen(true)

    // Clear any selected prospect when user starts typing
    setSelectedProspect(null)

    // If user clears the field, reset everything
    if (!newValue) {
      setFilteredProspects(prospects.slice(0, 50))
      if (onChange) {
        onChange('')
      }
      return
    }

    // Don't call onChange during typing - only when selecting from dropdown
    // This prevents triggering validation/modals while user is searching
  }

  const handleProspectSelect = (prospect) => {
    setSelectedProspect(prospect)
    setSearchTerm('')
    setIsOpen(false)
    console.log('Prospect selected:', prospect.mserp_prospectid)
    if (onChange) {
      onChange(prospect.mserp_prospectid)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIsOpen(true)
    }
  }

  const displayValue = selectedProspect
    ? selectedProspect.mserp_prospectname || selectedProspect.mserp_prospectid
    : value || ''

  return (
    <div>
      {resolvedLabel && (
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          {resolvedLabel}
        </label>
      )}

      <div className="relative" ref={dropdownRef}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          disabled={loading}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            error ? 'border-red-500' : 'border-neutral-300'
          } ${loading ? 'bg-neutral-100 cursor-not-allowed' : 'bg-white'} ${className || ''}`}
          placeholder={resolvedPlaceholder}
          {...props}
        />

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
          </div>
        )}

        {isOpen && (
          <div className="absolute z-[9999] w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {loadError && (
              <div className="p-3 text-center text-red-600">
                {t('prospect.errorLoading', { error: loadError })}
              </div>
            )}

            {!loadError && loading && (
              <div className="p-3 text-center text-neutral-600">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  <span>{t('prospect.loading')}</span>
                </div>
              </div>
            )}

            {!loadError && !loading && filteredProspects.length === 0 && !searchTerm && (
              <div className="p-3 text-center text-neutral-600">
                {t('prospect.noProspects')}
              </div>
            )}

            {!loadError && !loading && filteredProspects.length === 0 && searchTerm && (
              <div className="p-3 text-center text-neutral-600">
                {t('prospect.noResults', { searchTerm })}
              </div>
            )}

            {!loadError && !loading && filteredProspects.length > 0 && (
              <div className="py-1">
                {!searchTerm && prospects.length > 50 && (
                  <div className="p-2 text-center text-sm text-neutral-500 border-b bg-neutral-50">
                    {t('prospect.showingFirst')}
                  </div>
                )}
                {filteredProspects.map((prospect, index) => (
                  <div
                    key={`${prospect.mserp_smmprospectentityid || prospect.mserp_prospectid}-${index}`}
                    onClick={() => handleProspectSelect(prospect)}
                    className="px-3 py-2 cursor-pointer hover:bg-neutral-100 transition-colors"
                  >
                    <div className="font-medium text-neutral-800">
                      {prospect.mserp_prospectname}
                    </div>
                    <div className="text-sm text-neutral-600">
                      {prospect.mserp_prospectid}
                    </div>
                  </div>
                ))}
                {searchTerm && filteredProspects.length === 50 && (
                  <div className="p-2 text-center text-sm text-neutral-500 border-t">
                    {t('prospect.showingFirstFiltered')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  )
}
